import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Sparkle, FloppyDisk, Trash } from "@phosphor-icons/react";

const CATS = ["intro", "follow-up", "coffee", "info-share", "check-in", "other"];

export default function Templates() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showAI, setShowAI] = useState(false);
  const [ai, setAi] = useState({ relationship_stage: "new", purpose: "coffee meeting invitation", tone: "warm", contact_name: "" });
  const [aiBusy, setAiBusy] = useState(false);

  const load = () => api.get("/templates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const newTemplate = () => setEditing({ name: "", category: "follow-up", subject: "", body: "" });
  const save = async () => {
    try {
      if (editing.id) await api.put(`/templates/${editing.id}`, editing);
      else await api.post("/templates", editing);
      toast.success("Saved");
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (t) => { if (!window.confirm("Delete?")) return; await api.delete(`/templates/${t.id}`); load(); };

  const runAI = async () => {
    setAiBusy(true);
    try {
      const r = await api.post("/ai/template", ai);
      setEditing({ name: ai.purpose, category: "follow-up", subject: r.data.subject, body: r.data.body });
      setShowAI(false);
      toast.success("AI draft ready");
    } catch (e) { toast.error("AI failed"); }
    finally { setAiBusy(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="templates-page">
      <Toaster theme="dark" />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">// templates</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Message library</h1>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowAI(true)} data-testid="ai-template-btn" className="btn-secondary flex items-center gap-2"><Sparkle weight="bold" /> AI draft</button>
          <button onClick={newTemplate} data-testid="new-template-btn" className="btn-primary flex items-center gap-2"><Plus weight="bold" /> New</button>
        </div>
      </div>

      {showAI && (
        <div className="border p-6 mb-8" style={{ borderColor: "var(--accent)" }} data-testid="ai-template-form">
          <div className="overline mb-4" style={{ color: "var(--accent)" }}>// AI draft generator</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="label-base">Stage</label>
              <select value={ai.relationship_stage} onChange={(e) => setAi({ ...ai, relationship_stage: e.target.value })} className="input-base" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="ai-stage">
                {["new", "warming", "engaged", "active", "dormant"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div><label className="label-base">Tone</label>
              <input value={ai.tone} onChange={(e) => setAi({ ...ai, tone: e.target.value })} className="input-base" data-testid="ai-tone" />
            </div>
            <div className="md:col-span-2"><label className="label-base">Purpose</label>
              <input value={ai.purpose} onChange={(e) => setAi({ ...ai, purpose: e.target.value })} className="input-base" data-testid="ai-purpose" placeholder="e.g. invite to coffee, share resource" />
            </div>
          </div>
          <div className="mt-5 flex gap-3">
            <button onClick={runAI} disabled={aiBusy} data-testid="ai-generate-btn" className="btn-primary flex items-center gap-2"><Sparkle weight="bold" /> {aiBusy ? "Drafting..." : "Generate"}</button>
            <button onClick={() => setShowAI(false)} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {editing && (
        <div className="border p-6 mb-8" style={{ borderColor: "var(--border)" }} data-testid="template-editor">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="label-base">Name</label>
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="input-base" data-testid="tmpl-name" />
            </div>
            <div><label className="label-base">Category</label>
              <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="input-base" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="tmpl-cat">
                {CATS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-2"><label className="label-base">Subject</label>
              <input value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} className="input-base" data-testid="tmpl-subject" />
            </div>
            <div className="md:col-span-2"><label className="label-base">Body — use {`{{name}} {{company}} {{event}} {{me}}`}</label>
              <textarea rows={6} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className="input-base"
                style={{ border: "1px solid var(--border)", padding: "12px" }} data-testid="tmpl-body" />
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <button onClick={save} data-testid="save-tmpl-btn" className="btn-primary flex items-center gap-2"><FloppyDisk weight="bold" /> Save</button>
            <button onClick={() => setEditing(null)} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
        {items.length === 0 && <div className="md:col-span-2 p-10 text-center text-[#C7DDE5] border-r border-b" style={{ borderColor: "var(--border)" }}>No templates yet</div>}
        {items.map((t) => (
          <div key={t.id} className="border-r border-b p-6 group" style={{ borderColor: "var(--border)" }} data-testid={`tmpl-card-${t.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="overline">{t.category}</div>
                <h3 className="font-display font-bold text-xl mt-1">{t.name}</h3>
              </div>
              <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition">
                <button onClick={() => setEditing(t)} className="overline hover:text-white" data-testid={`edit-tmpl-${t.id}`}>edit</button>
                <button onClick={() => remove(t)} className="overline hover:text-white" data-testid={`del-tmpl-${t.id}`}>delete</button>
              </div>
            </div>
            {t.subject && <div className="mt-3 text-sm font-mono text-[#C7DDE5]">{t.subject}</div>}
            <p className="mt-3 text-sm text-[#C7DDE5] whitespace-pre-wrap line-clamp-6">{t.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
